"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Table = exports.TableSubProvider = exports.useTable = exports.TableSubContext = exports.TableVariantType = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ThemeProvider_1 = require("../../utils/ThemeProvider");
const Utils_1 = require("../../utils/Utils");
const Spinner_1 = require("../Spinner/Spinner");
const Contants_1 = require("../../utils/Contants");
const Table_styles_1 = require("./styles/Table.styles");
const TableHead_1 = require("./TableHead");
const TableCell_1 = require("./TableCell");
const TableRow_1 = require("./TableRow");
const TableBody_1 = require("./TableBody");
const TablePagination_1 = require("./TablePagination");
const __DEV__ = process.env.NODE_ENV !== 'production';
var TableVariantType;
(function (TableVariantType) {
    TableVariantType["HEAD"] = "head";
    TableVariantType["BODY"] = "body";
    TableVariantType["FOOTER"] = "footer";
})(TableVariantType || (exports.TableVariantType = TableVariantType = {}));
const TableContext = (0, react_1.createContext)({
    count: 0,
    data: { columns: [], rows: [] },
    isLoading: false,
    page: 0,
    prepareRowData: () => [],
    rowsPerPage: 10,
    setPage: () => { },
    setRowsPerPage: () => { },
    setSortedColumn: () => { },
    showGrid: false,
    showStriped: false,
    sortedColumn: undefined,
});
exports.TableSubContext = (0, react_1.createContext)({
    variant: undefined,
});
const useTable = () => (0, react_1.useContext)(TableContext);
exports.useTable = useTable;
const TableSubProvider = (props) => {
    const { children, variant } = props;
    const providerValue = (0, react_1.useMemo)(() => {
        return { variant };
    }, [variant]);
    return (0, jsx_runtime_1.jsx)(exports.TableSubContext.Provider, { value: providerValue, children: children });
};
exports.TableSubProvider = TableSubProvider;
const Table = (props) => {
    var _a, _b;
    const { caption, children, className = '', data, id, isLoading = false, pagination = false, paginationOptions, showGrid = false, showStriped = false, sorting = false, stickyHeader = false, testId = 'table', } = props;
    const theme = (0, ThemeProvider_1.useTheme)();
    const styles = (0, Table_styles_1.getTableStyles)();
    const dti = (0, Utils_1.getTestIdAttribute)(testId);
    const [currentRowsPerPage, setCurrentRowsPerPage] = (0, react_1.useState)(pagination ? (_a = paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.rowsPerPage) !== null && _a !== void 0 ? _a : 10 : data.rows.length);
    const [currentPage, setCurrentPage] = (0, react_1.useState)(pagination ? (_b = paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.page) !== null && _b !== void 0 ? _b : 1 : 1);
    const [sortedColumn, setSortedColumn] = (0, react_1.useState)();
    if (!theme) {
        if (__DEV__) {
            console.error('`Table` component must be a child of a `ThemeProvider`');
        }
        return null;
    }
    const handleSetSort = (key) => {
        if (!(sortedColumn === null || sortedColumn === void 0 ? void 0 : sortedColumn.key) || sortedColumn.key !== key) {
            setSortedColumn({ key, type: Contants_1.SortType.ASC });
            // set page to 0 due to sort
            setCurrentPage(1);
        }
        else if ((sortedColumn === null || sortedColumn === void 0 ? void 0 : sortedColumn.key) === key) {
            if (sortedColumn.type === Contants_1.SortType.ASC)
                setSortedColumn({ key, type: Contants_1.SortType.DESC });
            else {
                setSortedColumn(undefined);
            }
            // set page to 0 due to sort
            setCurrentPage(1);
        }
    };
    const prepareRowData = () => {
        const sortedData = (0, Utils_1.sortData)(data.rows, sortedColumn === null || sortedColumn === void 0 ? void 0 : sortedColumn.key, sortedColumn === null || sortedColumn === void 0 ? void 0 : sortedColumn.type);
        if (!pagination)
            return sortedData;
        if (currentRowsPerPage) {
            return sortedData.slice((currentPage - 1) * currentRowsPerPage, (currentPage - 1) * currentRowsPerPage + currentRowsPerPage);
        }
        else
            return sortedData;
    };
    const handleSetPage = (page) => {
        setCurrentPage(page);
    };
    const handleSetRowsPerPage = (count) => {
        setCurrentRowsPerPage(count);
        setCurrentPage(1);
    };
    (0, react_1.useEffect)(() => {
        // used for custom methods passing in page and rowsPerPage from parent component (custom usage)
        if (paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.page)
            setCurrentPage(paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.page);
        if (paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.rowsPerPage)
            setCurrentRowsPerPage(paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.rowsPerPage);
    }, [paginationOptions]);
    (0, react_1.useEffect)(() => {
        var _a;
        // Used for Storybook controls
        if (pagination)
            setCurrentRowsPerPage((_a = paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.rowsPerPage) !== null && _a !== void 0 ? _a : 10);
        else {
            setCurrentRowsPerPage(data.rows.length);
            setCurrentPage(1);
        }
    }, [pagination]);
    const tableMemo = (0, react_1.useMemo)(() => {
        return {
            count: data.rows.length,
            data,
            isLoading,
            page: currentPage,
            prepareRowData,
            rowsPerPage: currentRowsPerPage,
            setPage: handleSetPage,
            setRowsPerPage: handleSetRowsPerPage,
            setSortedColumn: handleSetSort,
            showGrid,
            showStriped,
            sortedColumn,
        };
    }, [currentRowsPerPage, currentPage, sortedColumn, showGrid, showStriped, data]);
    return ((0, jsx_runtime_1.jsx)(TableContext.Provider, { value: tableMemo, children: (0, jsx_runtime_1.jsxs)("div", Object.assign({ id: id, className: `Table-local ${styles.tableContainer} ${className}` }, dti, { children: [caption && ((0, jsx_runtime_1.jsx)("p", Object.assign({ className: `Table-caption-local ${styles.caption}` }, (0, Utils_1.getTestIdAttribute)('tableCaption'), { children: caption }))), (0, jsx_runtime_1.jsx)("div", { className: `Table-tableWrapper-local ${styles.tableWrapper}`, children: (0, jsx_runtime_1.jsx)("table", { className: `Table-table-local ${styles.table} ${showGrid ? styles.grid : ''}`, children: children ? (children) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(TableHead_1.TableHead, { children: (0, jsx_runtime_1.jsx)(TableRow_1.TableRow, { children: data.columns.map((col) => ((0, jsx_runtime_1.jsx)(TableCell_1.TableCell, { accessor: col.accessor, align: col.align, allowSorting: col.sortable || sorting, sticky: col.sticky || stickyHeader, stickyOffset: { top: stickyHeader ? '0' : '', left: col.sticky ? '0' : '' }, children: col.name }, col.accessor))) }) }), (0, jsx_runtime_1.jsx)(TableBody_1.TableBody, { children: prepareRowData().map((row, index) => ((0, jsx_runtime_1.jsx)(TableRow_1.TableRow, { children: data.columns.map((col, subIndex) => ((0, jsx_runtime_1.jsx)(TableCell_1.TableCell, { accessor: col.accessor, align: col.align, sticky: col.sticky, stickyOffset: { left: col.sticky ? '0' : '' }, children: row[col.accessor] }, subIndex))) }, index))) })] })) }) }), pagination && ((0, jsx_runtime_1.jsx)(TablePagination_1.TablePagination, { disableNext: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.disableNext, handleNextPage: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.handleNextPage, handlePrevPage: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.handlePrevPage, handleRowsPerPage: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.handleRowsPerPageChange, isLoading: isLoading, rowsPerPageOptions: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.rowsPerPageOptions, showRowsPerPage: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.showRowsPerPage, showRowsPrompt: paginationOptions === null || paginationOptions === void 0 ? void 0 : paginationOptions.showRowsPrompt })), isLoading && ((0, jsx_runtime_1.jsx)("div", { className: `Table-loading-local ${styles.loading}`, children: (0, jsx_runtime_1.jsx)(Spinner_1.Spinner, { className: styles.spinner }) }))] })) }));
};
exports.Table = Table;
